import { StepperSelectionEvent, CdkStep } from '@angular/cdk/stepper';
import { ChangeDetectorRef, Component, effect, EventEmitter, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  GetGradingQuestionsAction,
  SetSelectedTradeInStepIndexAction,
  ValidateImeiFailureAction,
  ValidateImeiSuccessAction,
} from '@app/sales/states/customer-product-trade-in/customer-product-trade-in.actions';
import { TradeInSelectors } from '@app/sales/states/customer-product-trade-in/customer-product-trade-in.selectors';
import { ExistingImeiValidator, ImeiTradeInValidator, ImeiValidator } from '@app/sales/validators/imei-validator';
import { Regex } from '@app/shared/enums/regex';
import { select, Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { OSAffectedControlType, TradeInImeiErrorType } from '../../../models/trade-in-enums';
import { TradeInHelperService } from '../../../services/trade-in-helper.service';
import { GradingQuestion, ImeiValidation } from '../../../models/trade-in-api.model';
import { TradeInSummaryComponent } from './trade-in-summary/trade-in-summary.component';
import { SpinnerComponent } from '@app/shared/components/atoms/spinner/spinner.component';
import { TradeInConditionComponent } from './trade-in-condition/trade-in-condition.component';
import { TradeInSpecificationsComponent } from './trade-in-specifications/trade-in-specifications.component';
import { IceButtonComponent } from '@app/shared/components/atoms/ice-button/ice-button.component';
import { TradeInTermsComponent } from './trade-in-terms/trade-in-terms.component';
import { NgxsFormDirective } from '@ngxs/form-plugin';
import { IceStepperComponent } from '@app/shared/components/molecules/ice-stepper/ice-stepper.component';
import { EquipmentService } from '@app/sales/services/equipment.service';

enum TradeInSteps {
  Terms = 0,
  Specifications = 1,
  Condition = 2,
  Summary = 3,
}

@Component({
  selector: 'app-trade-in-stepper',
  templateUrl: './trade-in-stepper.component.html',
  styleUrl: './trade-in-stepper.component.scss',
  imports: [
    IceStepperComponent,
    FormsModule,
    NgxsFormDirective,
    ReactiveFormsModule,
    CdkStep,
    TradeInTermsComponent,
    IceButtonComponent,
    TradeInSpecificationsComponent,
    TradeInConditionComponent,
    SpinnerComponent,
    TradeInSummaryComponent,
  ],
})
export class TradeInStepperComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private equipmentService = inject(EquipmentService);
  private tradeInHelperService = inject(TradeInHelperService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  @Output() infoClicked = new EventEmitter<OSAffectedControlType | string>();
  @Output() submitClicked = new EventEmitter<void>();

  selectedStep = select(TradeInSelectors.selectedStep);
  interactedSteps = select(TradeInSelectors.interactedSteps);
  gradingQuestions = select(TradeInSelectors.gradingQuestions);
  hasAcceptedTradeInVoucher = select(TradeInSelectors.hasAcceptedTradeInVoucher);

  deactivatedSteps: number[] = [];
  tradeInForm: FormGroup;
  subscriptions$: Subscription[] = [];

  get specificationsForm(): FormGroup {
    return this.tradeInForm.get('specifications') as FormGroup;
  }
  get showSpecificationsFormError(): boolean | undefined {
    return (this.specificationsForm.invalid && this.deactivatedSteps?.includes(TradeInSteps.Specifications)) ?? undefined;
  }

  get conditionForm(): FormGroup {
    return this.tradeInForm.get('condition') as FormGroup;
  }
  get conditionFormControlCount(): number {
    return Object.keys(this.conditionForm.controls).length;
  }
  get showConditionsFormError(): boolean | undefined {
    return (this.conditionForm.invalid && this.deactivatedSteps?.includes(TradeInSteps.Condition)) ?? undefined;
  }

  constructor() {
    effect(() => {
      this.addConditionForm(this.gradingQuestions());
    });
    effect(() => {
      this.deactivatedSteps = [...this.interactedSteps()];
    });
  }

  ngOnInit(): void {
    this.tradeInForm = this.buildForm();

    this.store.dispatch(new SetSelectedTradeInStepIndexAction(this.selectedStep() ?? TradeInSteps.Terms));
    this.addConditionForm(this.gradingQuestions());
    this.store.dispatch(new GetGradingQuestionsAction());

    if (this.hasAcceptedTradeInVoucher()) {
      this.tradeInForm.disable();
    }
  }

  private buildForm(): FormGroup {
    return new FormGroup({
      specifications: new FormGroup({
        imei: new FormControl(
          '',
          [
            Validators.required,
            Validators.minLength(15),
            Validators.maxLength(16),
            Validators.pattern(Regex.IMEI),
            ImeiValidator(),
            ExistingImeiValidator(this.tradeInHelperService),
          ],
          [
            ImeiTradeInValidator(
              this.equipmentService,
              this.tradeInHelperService,
              (result: ImeiValidation) => this.onImeiTradeInValidationSuccess(result),
              (error: TradeInImeiErrorType) => this.onImeiTradeInValidationFailure(error)
            ),
          ]
        ),
        storage: new FormControl('', [Validators.required]),
      }),
    });
  }

  private addConditionForm(questions: GradingQuestion[]): void {
    this.tradeInForm.addControl('condition', new FormGroup({}));
    questions.forEach(question => {
      (this.tradeInForm.get('condition') as FormGroup).addControl(
        question.questionType,
        new FormControl('', [Validators.required])
      );
    });
    this.changeDetectorRef.detectChanges();
  }

  onStepperChange(event: StepperSelectionEvent): void {
    this.deactivatedSteps.push(event.previouslySelectedIndex);
    this.store.dispatch(new SetSelectedTradeInStepIndexAction(event.selectedIndex));
  }

  private onImeiTradeInValidationSuccess(imeiValidationResult: ImeiValidation): void {
    this.specificationsForm.updateValueAndValidity();
    this.store.dispatch(new ValidateImeiSuccessAction(imeiValidationResult, this.specificationsForm.get('imei').value));
  }

  private onImeiTradeInValidationFailure(errorMessage: TradeInImeiErrorType): void {
    this.store.dispatch(new ValidateImeiFailureAction(errorMessage));
  }

  ngOnDestroy(): void {
    this.subscriptions$.forEach(subscription => subscription.unsubscribe());
  }
}
